import { cn } from '@shared/lib/cn';

/**
 * Jeu d'icônes SVG inline (style « stroke », inspiré de Lucide/Feather).
 * Aucune dépendance externe : chaque icône est un tracé SVG déclaré ici.
 * Toutes sont décoratives (aria-hidden) : le sens est porté par le texte.
 */
export type IconName =
  | 'dashboard'
  | 'receipt'
  | 'document'
  | 'sparkles'
  | 'settings'
  | 'help'
  | 'search'
  | 'plus'
  | 'chevron-down'
  | 'upload'
  | 'check'
  | 'alert'
  | 'spinner'
  | 'arrow-left'
  | 'dots'
  | 'filter'
  | 'menu'
  | 'panel-left'
  | 'panel-right'
  | 'bell'
  | 'trending-up'
  | 'wallet'
  | 'chart'
  | 'eye'
  | 'trash'
  | 'check-circle'
  | 'x';

const PATHS: Record<IconName, string> = {
  dashboard: 'M3 3h7v9H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 16h7v5H3z',
  receipt: 'M5 3h14v18l-2.5-1.5L14 21l-2.5-1.5L9 21l-2.5-1.5L5 21V3zM9 8h6M9 12h6',
  document: 'M6 2h9l5 5v15H6V2zM14 2v6h6M9 13h6M9 17h6',
  sparkles:
    'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3zM19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15z',
  settings:
    'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9c.26.6.85 1 1.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z',
  help: 'M12 22a10 10 0 100-20 10 10 0 000 20zM9.1 9a3 3 0 015.8 1c0 2-3 3-3 3M12 17h.01',
  search: 'M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.35-4.35',
  plus: 'M12 5v14M5 12h14',
  'chevron-down': 'M6 9l6 6 6-6',
  upload: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12',
  check: 'M20 6L9 17l-5-5',
  alert:
    'M12 9v4M12 17h.01M10.3 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.7 3.86a2 2 0 00-3.4 0z',
  spinner: 'M21 12a9 9 0 11-6.22-8.56',
  'arrow-left': 'M19 12H5M12 19l-7-7 7-7',
  dots: 'M12 13a1 1 0 100-2 1 1 0 000 2zM19 13a1 1 0 100-2 1 1 0 000 2zM5 13a1 1 0 100-2 1 1 0 000 2z',
  filter: 'M22 3H2l8 9.46V19l4 2v-8.54L22 3z',
  menu: 'M4 6h16M4 12h16M4 18h16',
  'panel-left': 'M3 5h18v14H3zM9 5v14',
  'panel-right': 'M3 5h18v14H3zM15 5v14',
  bell: 'M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0',
  'trending-up': 'M23 6l-9.5 9.5-5-5L1 18M17 6h6v6',
  wallet:
    'M21 12V7H5a2 2 0 01-2-2 2 2 0 012-2h14v4M3 5v14a2 2 0 002 2h16v-5M18 12a2 2 0 000 4h4v-4h-4z',
  chart: 'M3 3v18h18M8 17V9m4 8V5m4 12v-4',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7zM15 12a3 3 0 11-6 0 3 3 0 016 0z',
  trash:
    'M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6',
  'check-circle': 'M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3',
  x: 'M18 6L6 18M6 6l12 12',
};

export interface IconProps {
  name: IconName;
  className?: string;
  /** Taille en px (défaut 18). */
  size?: number;
}

export function Icon({ name, className, size = 18 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', name === 'spinner' && 'animate-spin', className)}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
